/**
 * useMatchCelebration
 *
 * Shared glue for firing the mutual-match toast from the swipe decks. Both the
 * pre-event (`/spark`) and post-event (`/event/:sparkId/match`) decks record a
 * like via `PUT /user/relationship`; when the response's `mutualMatch` flag is
 * set, the swipe just completed a two-way like. Call `celebrate(profile)` with
 * the person swiped on and this hook pops the `MatchNotification` in the
 * lower-right — confetti, faces, and a "View Your Matches" CTA — auto-dismissing
 * after a few seconds.
 *
 * Pass the pairing's category when the caller knows it: a mutual Spark on the
 * post-event Friends tab is a friendship, and the default copy tells the two
 * people they liked each other. The /spark deck can omit it — Friends Only
 * users are excluded from that deck server-side, so it is always dating.
 *
 * Usage:
 *   const { celebrate, matchCelebration } = useMatchCelebration();
 *   // …after a successful like whose response.mutualMatch is true:
 *   celebrate(profile, category);
 *   // …in JSX:
 *   {matchCelebration}
 */

import { AnimatePresence } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { UserProfile } from "@greenflagdate/shared";
import { refreshUserState, useStoreData } from "../../lib/store/store";
import { primaryProfileImagePath } from "../../lib/profileMedia";
import { MatchNotification } from "./MatchNotification";

/** How long the toast lingers before auto-dismissing (ms). */
const AUTO_DISMISS_MS = 8000;

export function useMatchCelebration() {
  const navigate = useNavigate();
  const storeData = useStoreData();
  const [matched, setMatched] = useState<{
    profile: UserProfile;
    category: "dating" | "friend";
  } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    clearTimer();
    setMatched(null);
  }, [clearTimer]);

  const celebrate = useCallback(
    (profile: UserProfile, category: "dating" | "friend" = "dating") => {
      clearTimer();
      setMatched({ profile, category });
      timerRef.current = setTimeout(() => setMatched(null), AUTO_DISMISS_MS);
      // Re-fetch and cache /user/state so the freshly-formed match is already
      // in the store by the time the viewer opens /matches from the toast —
      // otherwise the page renders stale data missing this connection.
      void refreshUserState();
    },
    [clearTimer]
  );

  // Belt-and-suspenders: drop the pending timer if the host unmounts mid-toast.
  useEffect(() => clearTimer, [clearTimer]);

  // The signed-in viewer's own headshot for the near avatar in the pair. The
  // store user carries the same `media` map shape UserProfile expects.
  const viewerPhoto = storeData.user
    ? primaryProfileImagePath({ media: storeData.user.media } as UserProfile)
    : undefined;

  const matchCelebration = (
    <AnimatePresence>
      {matched && (
        // key by user id so a fresh match remounts the card and replays the
        // confetti burst rather than morphing the existing one.
        <MatchNotification
          key={matched.profile.id}
          name={
            matched.profile.firstName ??
            (matched.category === "friend" ? "your new friend" : "your match")
          }
          category={matched.category}
          matchPhoto={primaryProfileImagePath(matched.profile)}
          yourPhoto={viewerPhoto}
          onViewMatches={() => {
            dismiss();
            navigate("/matches");
          }}
          onDismiss={dismiss}
        />
      )}
    </AnimatePresence>
  );

  return { celebrate, matchCelebration };
}
